import type { ArrayCallback, ArrayPredicate, ArrayReducer } from 'Core/types';
import { uniqBy, uniqueStrings } from 'Utils/array';

import FacetValue, { type FacetValueBase } from './facetValue';
import WeightedColor from './weightedColor';

type WeightedColorsInput = FacetValueBase[] | string[] | WeightedColor[];

export default class WeightedColorsArray {
  static toggleAndNormNotModified(
    colorValues: FacetValueBase[],
    value: FacetValueBase,
    wasSelected: boolean,
  ): FacetValueBase[] {
    return (wasSelected ? removeAndNormNotModified : addAndNormNotModified)(colorValues, value);
  }

  _values: WeightedColor[];

  constructor(values: WeightedColorsInput = []) {
    if (isWeightedColorArray(values)) {
      this._values = uniqBy(values, 'term');
      return;
    }

    if (isStringArray(values)) {
      this._values = uniqueStrings(values).map((v) => new WeightedColor(v));
      return;
    }

    this._values = uniqBy(values, FacetValue.valueKey).map((v) => new WeightedColor(v.term));
  }

  // other

  toFacetValues(field: string): FacetValueBase[] {
    return this.map(({ term, color: value }) => ({ field, term, value }));
  }

  // array interface

  get(i: number): WeightedColor {
    return this._values[i];
  }

  set(i: number, value: WeightedColor): void {
    this._values[i] = value;
  }

  get length(): number {
    return this._values.length;
  }

  filter(func: ArrayPredicate<WeightedColor>, thisArg?: unknown): WeightedColorsArray {
    return new WeightedColorsArray(this._values.filter(func, thisArg).map((v) => v.term));
  }

  forEach(func: ArrayCallback<WeightedColor>, thisArg?: unknown): void {
    this._values.forEach(func, thisArg);
  }

  map<R>(func: ArrayCallback<WeightedColor, R>, thisArg?: unknown): R[] {
    return this._values.map(func, thisArg);
  }

  reduce<R>(func: ArrayReducer<WeightedColor, R>, init: R): R {
    return this._values.reduce<R>(func, init);
  }

  every(func: ArrayPredicate<WeightedColor>, thisArg: unknown): boolean {
    return this._values.every(func, thisArg);
  }
}

function isWeightedColorArray(values: WeightedColorsInput): values is WeightedColor[] {
  return values[0] instanceof WeightedColor;
}

function isStringArray(values: WeightedColorsInput): values is string[] {
  return typeof values[0] === 'string';
}

function addAndNormNotModified(colorValues: FacetValueBase[], value: FacetValueBase): FacetValueBase[] {
  const colors = new WeightedColorsArray(colorValues);
  const color = new WeightedColor(value.term);
  if (colors.length) {
    const notModifiedWeight = Math.round(100 / colors.length);
    const newWeight = Math.round(100 / (colors.length + 1));

    colors._values.filter((c) => c.weight === notModifiedWeight).forEach((c) => (c.weight = newWeight));
    color.weight = newWeight;
  }
  colors._values.push(color);
  return colors.toFacetValues(value.field);
}

function removeAndNormNotModified(colorValues: FacetValueBase[], value: FacetValueBase): FacetValueBase[] {
  const colors = new WeightedColorsArray(
    colorValues.filter((v) => FacetValue.valueKey(v) !== FacetValue.valueKey(value)),
  );
  if (colors.length) {
    const notModifiedWeight = Math.round(100 / (colors.length + 1));
    const newWeight = Math.round(100 / colors.length);

    colors._values.filter((c) => c.weight === notModifiedWeight).forEach((c) => (c.weight = newWeight));
    return colors.toFacetValues(value.field);
  }
  return [];
}
